import { useEffect, useState } from "react";
import { ArrowLeft, Layers, PartyPopper, Shirt } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import { useAuth } from "../../components/auth/AuthGate";
import { listenToCelebrityDesigns } from "../../services/celebrityDesignService";
import { submitTailorQuoteRequest } from "../../services/tailorQuoteService";
import TailorQuoteContactFields, { emptyQuoteContact, isQuoteContactValid } from "../../components/quotes/TailorQuoteContactFields";

export default function CelebrityDesignDetailPage() {
  const { designId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, openAuth } = useAuth();
  const [design, setDesign] = useState(location.state?.design || null);
  const [requestingQuote, setRequestingQuote] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [quoteContact, setQuoteContact] = useState(() => emptyQuoteContact());
  useEffect(() => {
    setQuoteContact((current) => ({
      ...current,
      phone: current.phone || String(user?.phoneNumber || "").replace(/\D/g, "").slice(-10),
    }));
  }, [user]);
  useEffect(
    () =>
      design
        ? undefined
        : listenToCelebrityDesigns(
            (items) =>
              setDesign(items.find((item) => item.id === designId) || null),
            () => {},
          ),
    [design, designId],
  );
  if (!design)
    return (
      <div className="min-h-screen bg-[#FFF8F2]">
        <Navbar />
        <main className="pt-32 text-center">Loading design…</main>
      </div>
    );
  const requestQuote = async () => {
    if (!user) {
      openAuth();
      return;
    }
    if (requestingQuote) return;
    setRequestingQuote(true);
    setRequestError("");
    try {
      const result = await submitTailorQuoteRequest({
        customer: {
          uid: user.uid,
          name: user.displayName || "",
          email: user.email || "",
          phone: quoteContact.phone,
        },
        garmentName: design.name,
        designImageDataUrl: design.imageUrl,
        designFileName: `${design.id}.jpg`,
        notes: design.description || "",
        draftId: design.id,
        serviceType: "my_design",
        garmentId: "stitching_my_design",
        ...quoteContact,
      });
      sessionStorage.setItem(
        "stichb_design_draft",
        JSON.stringify({
          source: "uploaded_design",
          designId: design.id,
          designName: design.name,
          garmentId: "stitching_my_design",
          garmentName: design.name,
          imageUrl: design.imageUrl,
          previewUrl: design.imageUrl,
          imageDataUrl: design.imageUrl,
          fileName: `${design.id}.jpg`,
          notes: design.description,
          description: design.description,
          fabric: design.fabricGuidance,
          fabricTypeIds: design.fabricTypeIds || [],
          quoteRequestId: result.id,
        }),
      );
      navigate(
        "/category/celebrity/section/designs/item/stitching_my_design/design-confirmation",
      );
    } catch (error) {
      setRequestError(
        error?.message || "Unable to request a tailor quotation.",
      );
      setRequestingQuote(false);
    }
  };
  const details = [
    [PartyPopper, "Best occasions", design.occasion],
    [Layers, "Recommended fabrics", design.fabricGuidance],
    [Shirt, "How to wear this look", design.usageGuidance],
  ];
  return (
    <div className="min-h-screen bg-[#FFF8F2]">
      <Navbar onBook={() => navigate("/#services")} />
      <main className="pt-28 pb-24">
        <section className="max-w-[1200px] mx-auto px-6 md:px-12">
          <button
            onClick={() => navigate("/celebrity-designs")}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#F46524]"
          >
            <ArrowLeft size={16} /> All celebrity designs
          </button>
          <div className="grid lg:grid-cols-2 gap-10 mt-8">
            <div className="aspect-[4/5] overflow-hidden bg-[#FFF0E8]">
              <img
                src={design.imageUrl}
                alt={design.name}
                className="w-full h-full object-contain object-center"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            </div>
            <div className="lg:py-8">
              <div className="font-label text-[#F46524]">
                Worn by {design.celebrityName}
              </div>
              <h1 className="font-display text-4xl md:text-6xl mt-4">
                {design.name}
              </h1>
              <p className="text-[#5b5a56] leading-relaxed mt-6">
                {design.description}
              </p>
              <div className="mt-8 space-y-6">
                {details
                  .filter(([, , body]) => body)
                  .map(([Icon, title, body]) => (
                    <div key={title} className="flex gap-4">
                      <span className="w-11 h-11 rounded-full bg-[#FFEDDD] text-[#F46524] flex items-center justify-center shrink-0">
                        <Icon size={19} />
                      </span>
                      <div>
                        <h2 className="font-semibold">{title}</h2>
                        <p className="text-[#5b5a56] mt-1 leading-relaxed">
                          {body}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
              {requestError && (
                <p className="mt-5 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {requestError}
                </p>
              )}
              <div className="mt-6">
                <TailorQuoteContactFields value={quoteContact} onChange={setQuoteContact} />
              </div>
              <button
                disabled={requestingQuote || !isQuoteContactValid(quoteContact)}
                onClick={requestQuote}
                className="btn-primary w-full justify-center mt-10 disabled:opacity-60"
              >
                {requestingQuote
                  ? "Adding to custom orders..."
                  : "Get tailor quotation"}
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

